import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Form,
  FormTextarea,
  Text,
} from '@shared/components';
import { useSnackbar } from '@shared/snackbar';
import { zodResolver } from '@shared/utils';
import { FormProvider, useForm } from 'react-hook-form';

import { useRevokeExamCenterLocationPermission } from '../../hooks/use-revoke-exam-center-location-permission';

import {
  type RevokeExamCenterLocationPermissionFormInput,
  RevokeExamCenterLocationPermissionFormSchema,
} from './schema';

interface RevokeExamCenterLocationPermissionModalProps {
  open: boolean;
  onClose: () => void;
  examCenterLocationId: string;
}

export default function RevokeExamCenterLocationPermissionModal(
  props: Readonly<RevokeExamCenterLocationPermissionModalProps>,
) {
  const { open: isOpen, onClose, examCenterLocationId } = props;

  const { showSnackbar } = useSnackbar();

  const formContext = useForm<RevokeExamCenterLocationPermissionFormInput>({
    resolver: zodResolver(RevokeExamCenterLocationPermissionFormSchema),
    defaultValues: { reason: '' },
  });

  const { handleSubmit, reset, formState } = formContext;

  const [revokePermission, { loading }] = useRevokeExamCenterLocationPermission();

  const handleRevoke = async (data: RevokeExamCenterLocationPermissionFormInput) => {
    try {
      await revokePermission({
        examCenterLocationId,
        remark: data.reason,
      });
      showSnackbar({ message: 'ยกเลิกสิทธิ์การใช้งานสถานที่สอบเรียบร้อยแล้ว', variant: 'success' });
      onClose();
    } catch {
      showSnackbar({
        message: 'ยกเลิกสิทธิ์การใช้งานสถานที่สอบไม่สำเร็จ กรุณาลองใหม่อีกครั้ง',
        variant: 'error',
      });
    }
  };

  return (
    <FormProvider {...formContext}>
      <Dialog
        open={isOpen}
        onClose={onClose}
        maxWidth='sm'
        fullWidth
        disableEscapeKeyDown={formState.isSubmitting}
        slotProps={{
          paper: {
            component: Form,
            onSubmit: handleSubmit(handleRevoke),
          },
          transition: {
            onExited: () => reset(),
          },
        }}
      >
        <DialogTitle onClose={onClose} loading={formState.isSubmitting}>
          ยกเลิกสิทธิ์การใช้งานสถานที่สอบ?
        </DialogTitle>
        <DialogContent
          dividers
          sx={{ display: 'flex', flexDirection: 'column', gap: (theme) => theme.spacing(4) }}
        >
          <Text>
            เมื่อยกเลิกสิทธิ์แล้ว{' '}
            <Text component='span' sx={{ fontWeight: 'bold' }}>
              ศูนย์ทดสอบจะไม่สามารถใช้งานสถานที่สอบนี้ได้อีก
            </Text>{' '}
            กรุณาตรวจสอบความถูกต้องให้เรียบร้อยก่อนดำเนินการต่อ
          </Text>

          <FormTextarea
            name='reason'
            label='กรุณาระบุสาเหตุที่ยกเลิก'
            required
            fullWidth
            minRows={1}
            placeholder='กรุณาระบุสาเหตุที่ยกเลิก'
          />
        </DialogContent>
        <DialogActions>
          <Button
            size='large'
            variant='outlined'
            color='secondary'
            onClick={onClose}
            disabled={formState.isSubmitting || loading}
          >
            ยกเลิก
          </Button>
          <Button
            size='large'
            type='submit'
            color='error'
            loading={formState.isSubmitting || loading}
          >
            ยืนยันยกเลิกสิทธิ์การใช้งาน
          </Button>
        </DialogActions>
      </Dialog>
    </FormProvider>
  );
}
